import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonModal from "../../../components/modals/commonModel";
import CommonTable from "../../../components/table/table";
import { useCommonFetch } from "../../../../hooks/useCommonFetch";
import { useEffect, useState, useRef } from "react";
import CommonForm from "../../../components/form/form";
import { useSelector } from "react-redux";
import {
  createSalaryStructure,
  deleteSalaryStructure,
  getSalaryStructureList,
  updateSalaryStructure,
} from "../../../../services/workspace/salaryStructure.service";
import DefaultButton from "../../../components/buttons/defaultButton";
import { IoMdAdd } from "react-icons/io";
import { HiCurrencyRupee } from "react-icons/hi";
import toast from "react-hot-toast";

const actions_type = {
  DELETE: "DELETE",
  ADD_EDIT: "ADD_EDIT",
};

const items = [{ label: "Manage Salary Structure", path: "" }];

const ManageSalaryStructure = () => {
  const [isEditValue, setIsEditValue] = useState(null);
  const [components, setComponents] = useState([]);
  const [activeTab, setActiveTab] = useState("earning"); // "earning" or "deduction"
  const [annualCtcValue, setAnnualCtcValue] = useState(0); // Track CTC for summary
  const [newComponent, setNewComponent] = useState({
    name: "",
    percentage: "",
    taxable: true,
    type: "earning", // "earning" or "deduction"
  });
  const formRef = useRef(null);
  const { limit } = useSelector((state) => state.global);

  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    search,
    selectedId,
    action,
    handleSearchChange,
    handlePageChange,
    openModal,
    closeModal,
    handleCreate,
    handleUpdate,
    handleDelete,
    sortingUIProps,
    fetchData,
  } = useCommonFetch(getSalaryStructureList, {
    dataPath: "salaryStructure.data",
    totalPath: "salaryStructure.meta.total",
    errorMessage: "Failed to fetch salary structure data!",
    debounceDelay: 500,
    sortingOptions: [
      { label: "Name", field: "name" },
      { label: "Date", field: "created_at" },
    ],
    sortingModule: "salaryStructure",
    sortingDisableText: "Disabled",
    sortingShowDisableToggle: false,
    dataTransformer: (data) => {
      if (Array.isArray(data)) {
        const transformedData = data.map((item) => ({
          ...item,
          // Store original salaryComponent for edit
          salaryComponent: item.salaryComponent || [],
          id: {
            data: item.uuid,
            className: "",
            sticky: false,
            variant: "text",
            customCases: null,
          },
          name: {
            data: item.name,
            className: "",
            sticky: false,
            variant: "name",
            customCases: null,
          },
          description: {
            data: item.description,
            className: "",
            sticky: false,
            variant: "text",
            customCases: null,
          },
          annual_ctc: {
            data: item.annual_ctc,
            className: "",
            sticky: false,
            variant: "number",
            customCases: null,
          },
          components_count: {
            data: item.salaryComponent?.length || 0,
            className: "",
            sticky: false,
            variant: "text",
            customCases: null,
          },
          created_at: {
            data: item.created_at,
            className: "",
            sticky: false,
            variant: "date-time",
            customCases: null,
          },
          status: {
            data: item.status,
            className: "",
            sticky: false,
            variant: "status",
            customCases: null,
          },
          actions: {
            data: item.id,
            className: "",
            sticky: false,
            variant: "",
            customCases: null,
          },
        }));

        const actionButton = {
          delete: (item) => {
            handleDelete(item.id.data, deleteSalaryStructure, {
              successMessage: "Salary structure deleted successfully!",
              errorMessage: "Failed to delete salary structure!",
            });
          },
          edit: (item) => {
            openModal(item, actions_type.ADD_EDIT);
            
            // Get original salaryComponent from item (stored during transform)
            const originalComponents = item.salaryComponent || [];
            
            // Transform salaryComponent to match component format
            const transformedComponents = originalComponents.map((comp) => ({
              id: comp.id || comp.uuid,
              name: comp.name || "",
              percentage: comp.value || 0,
              taxable: comp.is_taxable === 1,
              type: comp.type || "earning",
            }));
            
            const InitialData = {
              name: item.name.data,
              description: item.description?.data || "",
              annual_ctc: item.annual_ctc?.data || "",
              id: item.id.data,
              components: transformedComponents,
            };
            setIsEditValue(InitialData);
            setComponents(transformedComponents);
            setActiveTab("earning"); // Reset to earnings tab
            setAnnualCtcValue(parseFloat(item.annual_ctc?.data) || 0); // Set CTC for summary
          },
        };

        return {
          data: transformedData,
          action: actionButton,
        };
      }
      return { data: [], action: {} };
    },
  });

  // Load components when editing
  useEffect(() => {
    if (isEditValue?.components) {
      setComponents(isEditValue.components);
    } else {
      setComponents([]);
    }
  }, [isEditValue]);

  const handleAddComponent = () => {
    if (!newComponent.name.trim() || !newComponent.percentage) {
      toast.error("Please fill component name and percentage");
      return;
    }

    // Only check 100% limit for earnings
    if (activeTab === "earning") {
      const earningsTotal = components
        .filter((c) => c.type === "earning" || !c.type)
        .reduce((sum, comp) => sum + parseFloat(comp.percentage || 0), 0);
      const newTotal = earningsTotal + parseFloat(newComponent.percentage);

      if (newTotal > 100) {
        toast.error("Total earnings percentage cannot exceed 100%");
        return;
      }
    }

    setComponents([
      ...components,
      {
        id: `comp_${Date.now()}`,
        ...newComponent,
        type: activeTab,
        percentage: parseFloat(newComponent.percentage),
      },
    ]);
    setNewComponent({ name: "", percentage: "", taxable: true, type: activeTab });
  };

  const handleRemoveComponent = (id) => {
    setComponents(components.filter((comp) => comp.id !== id));
  };

  const handleUpdateComponent = (id, field, value) => {
    setComponents(
      components.map((comp) =>
        comp.id === id ? { ...comp, [field]: value } : comp
      )
    );
  };

  // Separate totals for earnings and deductions
  const earningsComponents = components.filter((c) => c.type === "earning" || !c.type);
  const deductionsComponents = components.filter((c) => c.type === "deduction");
  
  const totalEarningsPercentage = earningsComponents.reduce(
    (sum, comp) => sum + parseFloat(comp.percentage || 0),
    0
  );
  const totalDeductionsPercentage = deductionsComponents.reduce(
    (sum, comp) => sum + parseFloat(comp.percentage || 0),
    0
  );
  const totalPercentage = totalEarningsPercentage; // For validation (earnings should be 100%)

  const comprehensiveFields = [
    {
      name: "name",
      label: "Structure Name",
      type: "text",
      placeholder: "e.g., Standard Structure, Executive Structure",
      required: true,
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      placeholder: "Enter structure description...",
    },
    {
      name: "annual_ctc",
      label: "Annual CTC (₹)",
      type: "number",
      placeholder: "Enter annual CTC",
      required: true,
      info: "This is the base CTC for calculating component percentages",
      onChange: (e) => setAnnualCtcValue(parseFloat(e.target.value) || 0),
    },
  ];

  const TableHeaderData = [
    { label: "Structure Name", key: "name" },
    { label: "Description", key: "description" },
    { label: "Annual CTC", key: "annual_ctc" },
    // { label: "Components", key: "components_count" },
    { label: "Created At", key: "created_at" },
    { label: "Status", key: "status" },
  ];

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (earningsComponents.length === 0) {
      toast.error("Please add at least one earning component");
      setSubmitting(false);
      return;
    }

    if (Math.abs(totalEarningsPercentage - 100) > 0.01) {
      toast.error("Total earnings percentage must equal 100%");
      setSubmitting(false);
      return;
    }

    const payload = {
      ...values,
      salaryComponent: components.map((comp) => ({
        name: comp.name,
        value: comp.percentage,
        is_taxable: comp.taxable === true ? 1 : 0,
        type: comp.type || "earning",
      })),
    };

    if (isEditValue) {
      await handleUpdate(payload, isEditValue.id, updateSalaryStructure, {
        successMessage: "Salary structure updated successfully!",
        errorMessage: "Failed to update salary structure!",
      });
    } else {
      await handleCreate(payload, createSalaryStructure, {
        successMessage: "Salary structure created successfully!",
        errorMessage: "Failed to create salary structure!",
      });
    }

    setSubmitting(false);
    setTimeout(() => {
      resetForm();
      setIsEditValue(null);
      setComponents([]);
    }, 1000);
  };

  const handleCancel = () => {
    setIsEditValue(null);
    setComponents([]);
    setActiveTab("earning");
    setAnnualCtcValue(0);
    setNewComponent({ name: "", percentage: "", taxable: true, type: "earning" });
    closeModal();
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className={"flex flex-col gap-2"}>
      <PageTableHeader
        isSearch
        onSearchChange={handleSearchChange}
        add
        onClickAdd={() => openModal("", actions_type.ADD_EDIT)}
        filter={false}
        shorting
        sortingModule="salaryStructure"
        onSortingChange={sortingUIProps?.onSortingChange}
        sortingProps={sortingUIProps}
        breadcrumbs={items}
      />

      <div className={"bg-white"}>
        <CommonTable
          search={search}
          TableHeaderData={TableHeaderData}
          tabledata={tableData.data}
          currentPage={currentPage}
          totalData={totalData}
          setCurrentPage={handlePageChange}
          loading={loading}
          action={tableData.action}
        />

        {action === actions_type.ADD_EDIT && (
          <CommonModal
            modalSize="4xl"
            headerTitle={
              selectedId ? "Edit Salary Structure" : "Add Salary Structure"
            }
            onClose={handleCancel}
            show={action === actions_type.ADD_EDIT}
          >
            <div className="flex flex-col gap-4">
              {/* Basic Info Form */}
              <div ref={formRef}>
                <CommonForm
                  fields={comprehensiveFields}
                  initialData={isEditValue ? isEditValue : {}}
                  onSubmit={handleSubmit}
                  submitButtonText=""
                  columnsPerRow={1}
                  gap="gap-4"
                  fieldClassName="bg-white p-3 rounded shadow-sm"
                  showSubmitButton={false}
                />
              </div>

              {/* Components Section with Tabs */}
              <div className="border-t border-borderColor pt-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-theme">
                      Salary Components
                    </h3>
                    <p className="text-sm text-textLight">
                      Manage earnings and deductions for this salary structure.
                    </p>
                  </div>
                  <div className="flex gap-4 text-right">
                    <div className="px-3 py-1 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-xs text-green-600">Earnings</p>
                      <p className={`text-lg font-semibold ${Math.abs(totalEarningsPercentage - 100) < 0.01 ? "text-green-600" : "text-orange-500"}`}>
                        {totalEarningsPercentage.toFixed(2)}%
                      </p>
                    </div>
                    <div className="px-3 py-1 bg-red-50 rounded-lg border border-red-200">
                      <p className="text-xs text-red-600">Deductions</p>
                      <p className="text-lg font-semibold text-red-600">
                        {totalDeductionsPercentage.toFixed(2)}%
                      </p>
                    </div>
                  </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-borderColor mb-4">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab("earning");
                      setNewComponent({ name: "", percentage: "", taxable: true, type: "earning" });
                    }}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === "earning"
                        ? "border-green-500 text-green-600 bg-green-50"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    💰 Earnings ({earningsComponents.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveTab("deduction");
                      setNewComponent({ name: "", percentage: "", taxable: false, type: "deduction" });
                    }}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === "deduction"
                        ? "border-red-500 text-red-600 bg-red-50"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    📉 Deductions ({deductionsComponents.length})
                  </button>
                </div>

                {/* Add Component Form */}
                <div className={`rounded-lg p-4 border mb-4 ${activeTab === "earning" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">{activeTab === "earning" ? "💰" : "📉"}</span>
                    <span className="text-sm font-medium text-gray-700">
                      Add {activeTab === "earning" ? "Earning" : "Deduction"} Component
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <input
                      type="text"
                      placeholder={activeTab === "earning" ? "e.g., Basic, HRA, DA" : "e.g., PF, ESI, TDS"}
                      value={newComponent.name}
                      onChange={(e) =>
                        setNewComponent({ ...newComponent, name: e.target.value })
                      }
                      className="h-10 rounded-md border border-borderColor bg-white px-3 text-sm text-theme"
                    />
                    <input
                      type="number"
                      placeholder="Percentage"
                      value={newComponent.percentage}
                      onChange={(e) =>
                        setNewComponent({
                          ...newComponent,
                          percentage: e.target.value,
                        })
                      }
                      min="0"
                      max="100"
                      step="0.01"
                      className="h-10 rounded-md border border-borderColor bg-white px-3 text-sm text-theme"
                    />
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={newComponent.taxable}
                        onChange={(e) =>
                          setNewComponent({
                            ...newComponent,
                            taxable: e.target.checked,
                          })
                        }
                        className="w-4 h-4 text-theme border-borderColor rounded"
                      />
                      <label className="text-sm text-theme">Taxable</label>
                    </div>
                    <DefaultButton
                      variant={activeTab === "earning" ? "default" : "danger"}
                      name={`Add ${activeTab === "earning" ? "Earning" : "Deduction"}`}
                      icon={<IoMdAdd />}
                      onClick={handleAddComponent}
                      disabled={!newComponent.name.trim() || !newComponent.percentage}
                    />
                  </div>
                </div>

                {/* Components List based on active tab */}
                {(() => {
                  const currentComponents = activeTab === "earning" ? earningsComponents : deductionsComponents;
                  const currentTotal = activeTab === "earning" ? totalEarningsPercentage : totalDeductionsPercentage;
                  
                  if (currentComponents.length > 0) {
                    return (
                      <div className={`bg-white border rounded-lg overflow-hidden ${activeTab === "earning" ? "border-green-200" : "border-red-200"}`}>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className={activeTab === "earning" ? "bg-green-50" : "bg-red-50"}>
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-semibold text-theme uppercase">
                                  Component Name
                                </th>
                                <th className="px-4 py-3 text-right text-xs font-semibold text-theme uppercase">
                                  Percentage
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-theme uppercase">
                                  Taxable
                                </th>
                                <th className="px-4 py-3 text-center text-xs font-semibold text-theme uppercase">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-borderColor">
                              {currentComponents.map((comp) => (
                                <tr key={comp.id}>
                                  <td className="px-4 py-3">
                                    <input
                                      type="text"
                                      value={comp.name}
                                      onChange={(e) =>
                                        handleUpdateComponent(
                                          comp.id,
                                          "name",
                                          e.target.value
                                        )
                                      }
                                      className="w-full h-8 rounded-md border border-borderColor bg-white px-2 text-sm text-theme"
                                    />
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center justify-end gap-2">
                                      <input
                                        type="number"
                                        value={comp.percentage}
                                        onChange={(e) => {
                                          const val = parseFloat(e.target.value) || 0;
                                          if (val >= 0 && val <= 100) {
                                            handleUpdateComponent(
                                              comp.id,
                                              "percentage",
                                              val
                                            );
                                          }
                                        }}
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        className="w-24 h-8 rounded-md border border-borderColor bg-white px-2 text-sm text-theme text-right"
                                      />
                                      <span className="text-sm text-textLight">%</span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <input
                                      type="checkbox"
                                      checked={comp.taxable}
                                      onChange={(e) =>
                                        handleUpdateComponent(
                                          comp.id,
                                          "taxable",
                                          e.target.checked
                                        )
                                      }
                                      className="w-4 h-4 text-theme border-borderColor rounded"
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <DefaultButton
                                      variant="danger"
                                      name="Remove"
                                      onClick={() => handleRemoveComponent(comp.id)}
                                      classnames="text-xs"
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className={activeTab === "earning" ? "bg-green-50" : "bg-red-50"}>
                              <tr>
                                <td className="px-4 py-3 font-semibold text-theme">
                                  Total {activeTab === "earning" ? "Earnings" : "Deductions"}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  <span className={`font-semibold ${activeTab === "earning" ? (Math.abs(currentTotal - 100) < 0.01 ? "text-green-600" : "text-orange-500") : "text-red-600"}`}>
                                    {currentTotal.toFixed(2)}%
                                  </span>
                                </td>
                                <td colSpan={2}></td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    );
                  }
                  
                  return (
                    <div className={`text-center py-8 rounded-lg border ${activeTab === "earning" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                      <span className="text-4xl mb-2 block">{activeTab === "earning" ? "💰" : "📉"}</span>
                      <p className="text-textLight">
                        No {activeTab === "earning" ? "earnings" : "deductions"} added yet. Add your first {activeTab === "earning" ? "earning" : "deduction"} component above.
                      </p>
                    </div>
                  );
                })()}

                {/* Summary Card */}
                {components.length > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-borderColor">
                    <h4 className="text-sm font-semibold text-theme mb-3">📊 Structure Summary</h4>
                    {(() => {
                      // Use tracked CTC value for real-time updates
                      const monthlyCtc = annualCtcValue / 12;
                      
                      const earningsAmount = (totalEarningsPercentage / 100) * monthlyCtc;
                      const deductionsAmount = (totalDeductionsPercentage / 100) * monthlyCtc;
                      const netAmount = earningsAmount - deductionsAmount;
                      
                      const formatCurrency = (amount) => {
                        return new Intl.NumberFormat('en-IN', {
                          style: 'currency',
                          currency: 'INR',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0,
                        }).format(amount);
                      };
                      
                      return (
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div className="text-center p-3 bg-green-50 rounded-lg">
                            <p className="text-xs text-gray-500">Total Earnings</p>
                            <p className="font-semibold text-green-600">{totalEarningsPercentage.toFixed(2)}%</p>
                            <p className="text-xs text-gray-400">{earningsComponents.length} components</p>
                            {monthlyCtc > 0 && (
                              <p className="text-sm font-bold text-green-700 mt-1">{formatCurrency(earningsAmount)}/mo</p>
                            )}
                          </div>
                          <div className="text-center p-3 bg-red-50 rounded-lg">
                            <p className="text-xs text-gray-500">Total Deductions</p>
                            <p className="font-semibold text-red-600">{totalDeductionsPercentage.toFixed(2)}%</p>
                            <p className="text-xs text-gray-400">{deductionsComponents.length} components</p>
                            {monthlyCtc > 0 && (
                              <p className="text-sm font-bold text-red-700 mt-1">-{formatCurrency(deductionsAmount)}/mo</p>
                            )}
                          </div>
                          <div className="text-center p-3 bg-blue-50 rounded-lg">
                            <p className="text-xs text-gray-500">Net Salary</p>
                            <p className="font-semibold text-blue-600">{(totalEarningsPercentage - totalDeductionsPercentage).toFixed(2)}%</p>
                            <p className="text-xs text-gray-400">of CTC</p>
                            {monthlyCtc > 0 && (
                              <p className="text-sm font-bold text-blue-700 mt-1">{formatCurrency(netAmount)}/mo</p>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {earningsComponents.length === 0 && deductionsComponents.length === 0 && (
                  <div className="text-center py-8 bg-primary-50 rounded-lg border border-borderColor mt-4">
                    <HiCurrencyRupee className="text-4xl text-textLight mx-auto mb-2" />
                    <p className="text-textLight">
                      No components added. Add your first component above.
                    </p>
                  </div>
                )}

                {/* Validation Message */}
                {components.length > 0 &&
                  Math.abs(totalPercentage - 100) > 0.01 && (
                    <div className="mt-3 bg-error-50 border border-error-200 rounded-lg p-3">
                      <p className="text-sm text-error-700">
                        Total percentage must equal 100%. Current:{" "}
                        {totalPercentage.toFixed(2)}%
                      </p>
                    </div>
                  )}
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 border-t border-borderColor pt-4">
                <DefaultButton
                  variant="secondary"
                  name="Cancel"
                  onClick={handleCancel}
                />
                <DefaultButton
                  variant="default"
                  name={isEditValue ? "Update" : "Create"}
                  onClick={async () => {
                    // Trigger form submission
                    const form = formRef.current?.querySelector('form');
                    if (form) {
                      const submitButton = form.querySelector('button[type="submit"]');
                      if (submitButton) {
                        submitButton.click();
                      } else {
                        // Fallback: create and dispatch submit event
                        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                        form.dispatchEvent(submitEvent);
                      }
                    }
                  }}
                />
              </div>
            </div>
          </CommonModal>
        )}
      </div>
    </div>
  );
};

export default ManageSalaryStructure;

